import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cx } from './cx';

/**
 * A card's list that scrolls inside the card: the missed calls, the bookings for later.
 *
 * A list cut by the card's edge cut a row flush against the bottom border — a number with no
 * room under it, which reads as broken rather than as "there is more". While there is more
 * below, the last few pixels fade out instead; scrolled to the end there is nothing to fade
 * and the last row ends as a row should. Watched by size and by content as well as by
 * scroll, because a list that grows under a dispatcher's eyes can start needing the fade
 * without anybody scrolling.
 */
export function ScrollList({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  const list = useRef<HTMLUListElement>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    const element = list.current;

    if (element === null) return;

    const check = () =>
      setMore(element.scrollHeight - element.scrollTop - element.clientHeight > 2);

    check();

    const resized = new ResizeObserver(check);
    resized.observe(element);

    const changed = new MutationObserver(check);
    changed.observe(element, { childList: true, subtree: true });

    element.addEventListener('scroll', check, { passive: true });

    return () => {
      resized.disconnect();
      changed.disconnect();
      element.removeEventListener('scroll', check);
    };
  }, []);

  return (
    <ul
      ref={list}
      className={cx('m-0 p-0 list-none max-h-[320px] overflow-y-auto', className)}
      style={
        more
          ? {
              maskImage: 'linear-gradient(to bottom, black calc(100% - 36px), transparent)',
              WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 36px), transparent)',
            }
          : undefined
      }
    >
      {children}
    </ul>
  );
}
